import React, { useState, useEffect } from 'react';
import styles from './Feedback.module.css';
import api from '../../utils/api';

function Feedback() {
    const [openFaqIndex, setOpenFaqIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        type: 'duvida',
        subject: '',
        message: ''
    });

    // Tenta pré-preencher o formulário com dados do utilizador logado no carregamento
    useEffect(() => {
        async function fetchUserData() {
            try {
                const token = JSON.parse(localStorage.getItem('token'));
                if (!token) return;

                const res = await api.get('/users/checkuser', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const user = res.data;
                if (user) {
                    setFormData(prev => ({
                        ...prev,
                        name: user.name || '',
                        email: user.email || ''
                    }));
                }
            } catch (err) {
                console.error('Erro ao obter dados do utilizador:', err);
            }
        }
        fetchUserData();
    }, []);

    const faqList = [
        {
            question: "Como funciona a assinatura dos planos?",
            answer: "A assinatura dá acesso imediato a todas as funcionalidades do sistema de acordo com o plano escolhido. A renovação pode ser mensal ou anual, podendo ser cancelada a qualquer momento sem taxas adicionais."
        },
        {
            question: "Posso alterar meu plano depois de já ter assinado?",
            answer: "Sim! Você pode fazer o upgrade ou downgrade do seu plano a qualquer momento diretamente pelo painel do seu criatório ou entrando em contato com nosso suporte."
        },
        {
            question: "Meus dados e histórico de genealogia ficam salvos em segurança?",
            answer: "Sim, utilizamos criptografia e backups diários na nuvem para garantir que os dados do seu plantel fiquem protegidos e acessíveis apenas por você."
        }
    ];

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setLoading(true);

        try {
            const token = JSON.parse(localStorage.getItem('token'));

            await api.post('/feedback/create', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSubmitted(true);
            setFormData(prev => ({
                ...prev,
                type: 'duvida',
                subject: '',
                message: ''
            }));

            setTimeout(() => setSubmitted(false), 5000);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao enviar sua mensagem. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <span className={styles.badge}>Dúvidas & Sugestões</span>
                <h2>Dúvidas Frequentes e Sugestões</h2>
                <p>Encontre respostas rápidas para as dúvidas mais comuns ou envie sua sugestão para nos ajudar a melhorar o sistema.</p>
            </div>

            {/* SEÇÃO 1: FAQ */}
            <section className={styles.faqSection}>
                <h3>Perguntas Frequentes (FAQ)</h3>
                <div className={styles.faqList}>
                    {faqList.map((item, index) => (
                        <div key={index} className={styles.faqItem}>
                            <button 
                                className={styles.faqQuestion} 
                                onClick={() => toggleFaq(index)}
                                aria-expanded={openFaqIndex === index}
                            >
                                <span>{item.question}</span>
                                <span className={styles.faqIcon}>
                                    {openFaqIndex === index ? '−' : '+'}
                                </span>
                            </button>
                            {openFaqIndex === index && (
                                <div className={styles.faqAnswer}>
                                    <p>{item.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* SEÇÃO 2: FORMULÁRIO */}
            <section className={styles.formSection}>
                <div className={styles.formHeader}>
                    <h3>Não encontrou a resposta ou tem uma sugestão?</h3>
                    <p>Preencha o formulário abaixo e nossa equipe analisará sua mensagem.</p>
                </div>

                {submitted && (
                    <div className={styles.successMessage} style={{ backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                        Sua mensagem foi enviada com sucesso! Agradecemos o seu contato.
                    </div>
                )}

                {errorMessage && (
                    <div className={styles.errorMessage} style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                        {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.inputRow}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="name">Seu Nome *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Digite seu nome"
                                required
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="email">Seu E-mail *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="seu@email.com"
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.inputRow}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="type">Tipo de Mensagem *</label>
                            <select
                                id="type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                required
                            >
                                <option value="duvida">Tenho uma Dúvida</option>
                                <option value="sugestao">Quero enviar uma Sugestão</option>
                                <option value="problema">Relatar um Problema</option>
                            </select>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="subject">Assunto *</label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="Ex: Ideia para nova tela / Dúvida sobre relatórios"
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="message">Sua Mensagem *</label>
                        <textarea
                            id="message"
                            name="message"
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Descreva detalhadamente sua dúvida ou sugestão..."
                            required
                        ></textarea>
                    </div>

                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                        {loading ? 'A enviar...' : 'Enviar Mensagem'}
                    </button>
                </form>
            </section>
        </div>
    );
}

export default Feedback;